import { CoinflowEnvs, CoinflowPurchase, CoinflowPurchaseProtection } from '@coinflowlabs/react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import { ethers } from 'ethers';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';
import { RuntimeConfiguration } from '@/configuration';
import { useWallet } from '@/lib';
import { StorageService } from '@/lib/services';
import { useMenuDetails } from '@/provider/MenuContext';

interface CoinFlowLayoutProps {
  open: boolean;
  data: {
    price: number;
    encodeData: string;
  };
  handleClose: () => void;
}

const CoinFlowLayout: React.FC<CoinFlowLayoutProps> = ({ open, data, handleClose }) => {
  const { address, provider } = useWallet();
  const { setMenuIndex } = useMenuDetails();
  const router = useRouter();

  const sendTransaction = useCallback(
    async (
      transaction: ethers.providers.TransactionRequest
    ): Promise<{
      hash: string;
    }> => {
      const getProvider = provider;
      const response = await getProvider.sendTransaction(transaction);

      return response;
    },
    [provider]
  );

  const signMessage = useCallback(
    async (message: string): Promise<string> => {
      const permitData = JSON.parse(message);
      const signer = provider.getSigner();

      return await signer._signTypedData(
        permitData.domain,
        {
          Permit: permitData.types.Permit
        },
        permitData.message
      );
    },
    [provider]
  );

  const isFree = useMemo(() => {
    return Number(data.price) === 0;
  }, [data]);

  const onSuccess = useCallback(
    async (data: any) => {
      try {
        handleClose();
        setMenuIndex(1);
        router.push('/collectible');
        StorageService.transactionHash.setValue(data?.hash);
      } catch (e) {
        throw e;
      }
    },
    [handleClose, router, setMenuIndex]
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          backgroundColor: '#000',
          padding: {
            lg: '0px 120px 120px 120px',
            md: '0px 120px 120px 120px',
            xs: '0  '
          },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          borderRadius: '12px'
        }}
      >
        <Stack
          sx={{
            width: '100%',
            maxWidth: '800px',
            height: '100dvh',
            borderRadius: '12px'
          }}
        >
          <CoinflowPurchaseProtection
            merchantId={RuntimeConfiguration.MERCHANT_ID as string}
            coinflowEnv={RuntimeConfiguration.COIN_FLOW_ENV as CoinflowEnvs}
          />
          <CoinflowPurchase
            loaderBackground="#090A0C"
            wallet={{
              address,
              sendTransaction,
              signMessage
            }}
            merchantId={RuntimeConfiguration.MERCHANT_ID as string}
            env={RuntimeConfiguration.COIN_FLOW_ENV as CoinflowEnvs}
            onSuccess={onSuccess}
            blockchain="eth"
            token={isFree ? undefined : '0x0000000000000000000000000000000000001010'}
            transaction={{
              transaction: {
                data: data.encodeData,
                to: RuntimeConfiguration.CONTRACT_ADDRESS
              },
              waitForHash: true
            }}
            amount={isFree ? undefined : data.price}
            chargebackProtectionData={[
              {
                productName: 'mercedes-mint',
                productType: 'digitalArt',
                quantity: 1,
                rawProductData: {
                  address,
                  amount: isFree ? 0 : data.price
                }
              }
            ]}
          />
        </Stack>
      </Box>
    </Modal>
  );
};

export default CoinFlowLayout;
